import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Col,
  Form,
  Row,
} from "Components/Core";
import { GeminiPromptObjectType } from "Interfaces/Pages/GeminiPromptsInterface";
import {
  updateGeminiPrompt,
  createGeminiPrompt,
  getMasterSportsExcluding,
} from "Api/Pages/GeminiPromptsApi";
import { setToastMessage } from "App/commonReducer";
import { useAppDispatch } from "App/hooks";
import { SUCCESS } from "App/constants";
import { SportObjectType } from "Interfaces/Pages/SportsInterface";

interface EditGeminiPromptModalProps {
  isModalVisible: boolean;
  prompt: GeminiPromptObjectType | null;
  existingPrompts: GeminiPromptObjectType[];
  toggleModal: () => void;
  onUpdateSuccess: () => void;
}

const EditGeminiPromptModal: React.FC<EditGeminiPromptModalProps> = ({
  isModalVisible,
  prompt,
  existingPrompts,
  toggleModal,
  onUpdateSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [promptText, setPromptText] = useState<string>("");
  const [selectedSportId, setSelectedSportId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [availableSports, setAvailableSports] = useState<SportObjectType[]>([]);

  const isEditMode = !!prompt;

  const fetchAvailableSports = useCallback(async () => {
    // Only fetch sports for create mode
    const excludeIds = existingPrompts
      .filter((p) => p.master_sport_id !== null)
      .map((p) => p.master_sport_id as number);
    const sports = await getMasterSportsExcluding(excludeIds);
    setAvailableSports(sports);
  }, [existingPrompts]);

  useEffect(() => {
    if (isModalVisible) {
      if (isEditMode && prompt) {
        // Edit mode: just set the prompt data, no need to fetch sports
        setPromptText(prompt.prompt || "");
        setSelectedSportId(prompt.master_sport_id);
      } else {
        // Create mode: reset form and fetch available sports
        setPromptText("");
        setSelectedSportId(null);
        fetchAvailableSports();
      }
    }
  }, [prompt, isEditMode, isModalVisible, fetchAvailableSports]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!promptText.trim()) {
      return;
    }

    if (isEditMode) {
      if (!prompt) return;
      setIsSubmitting(true);
      try {
        await updateGeminiPrompt(prompt.id, { prompt: promptText });
        dispatch(setToastMessage([["Prompt updated successfully"], SUCCESS]));
        onUpdateSuccess();
        toggleModal();
      } catch (error) {
        // Error handling is done in the API interceptor
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Create mode
      if (!selectedSportId) {
        return;
      }
      setIsSubmitting(true);
      try {
        await createGeminiPrompt({
          prompt: promptText,
          master_sport_id: selectedSportId,
        });
        dispatch(setToastMessage([["Prompt created successfully"], SUCCESS]));
        onUpdateSuccess();
        toggleModal();
      } catch (error) {
        // Error handling is done in the API interceptor
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    if (isEditMode && prompt) {
      setPromptText(prompt.prompt || "");
      setSelectedSportId(prompt.master_sport_id);
    } else {
      setPromptText("");
      setSelectedSportId(null);
    }
    toggleModal();
  };

  return (
    <Modal centered size="lg" show={isModalVisible} onHide={handleCancel}>
      <ModalHeader>
        {isEditMode ? "Edit Gemini Prompt" : "Create Gemini Prompt"}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <Row>
            <Col sm={12}>
              {isEditMode && prompt?.prompt_category === "exercise" ? (
                <Form.Group controlId="exercise">
                  <Form.Label className="fs-xs">Exercise</Form.Label>
                  <Form.Control
                    type="text"
                    value={prompt.exercise_type ?? "----"}
                    disabled
                    className="bg-light"
                  />
                </Form.Group>
              ) : isEditMode && prompt?.master_sport_id ? (
                <Form.Group controlId="sport">
                  <Form.Label className="fs-xs">Sport</Form.Label>
                  <Form.Control
                    type="text"
                    value={prompt.master_sport?.name || "----"}
                    disabled
                    className="bg-light"
                  />
                </Form.Group>
              ) : !isEditMode ? (
                <Form.Group controlId="sport">
                  <Form.Label className="fs-xs">Sport *</Form.Label>
                  <Form.Select
                    className="ss-select-control bg-white"
                    value={selectedSportId || ""}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      setSelectedSportId(Number(e.target.value) || null)
                    }
                    required
                  >
                    <option value="" disabled>
                      Select Sport
                    </option>
                    {availableSports.map((sport) => (
                      <option key={sport.id} value={sport.id}>
                        {sport.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              ) : null}
            </Col>
          </Row>
          <Row className="mt-3">
            <Col sm={12}>
              <Form.Group controlId="prompt">
                <Form.Label className="fs-xs">Prompt *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  className="border"
                  placeholder="Enter prompt text"
                  value={promptText}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setPromptText(e.target.value)
                  }
                  required
                />
              </Form.Group>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline-secondary"
            size="sm"
            className="px-4"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="px-4"
            type="submit"
            disabled={
              isSubmitting ||
              !promptText.trim() ||
              (!isEditMode && !selectedSportId)
            }
          >
            {isEditMode ? "Save" : "Create"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default EditGeminiPromptModal;
